import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import { useI18n } from "../../hooks/useTranslations";
import { useCostCentersApi } from "../../http/userCostCenters/api";
import { formatSalesRoles, User } from "../../http/users/types";
import { getMeasurmentTypes, MeasurmentType } from "../../types";
import { formatDateNumeric, isValidEmail } from "../../types/utils";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import ModalDialog from "../ui/ModalDialog";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Switch from "../ui/Switch";
import UserCostCentersDialog from "./UserCostCentersDialog";
import styles from "./UserForm.module.css";

export interface UserFormBody {
  firstName: string;
  lastName: string;
  email: string | null;
  birthDate: string | null;
  measurmentType: MeasurmentType | null;
  hidden: boolean;
  drivingLicenseExpiration: string;
  isRothoblaasUser: boolean;
  areNotificationsDisabled: boolean;
}

interface UserFormProps {
  user?: User | null;
  onSubmit: (formData: UserFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  reloadUser?: () => void;
}

const UserForm: FC<UserFormProps> = ({ onSubmit, user, isDisabled, isLoading, reloadUser }: UserFormProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);

  const userSettings = useUserSettings();

  const costCenterApi = useCostCentersApi();

  const [showCostCenterDialog, setShowCostCenterDialog] = useState(false);
  const [showHiddenInfo, setShowHiddenInfo] = useState(false);
  const [showIsRothoblaasInfo, setShowIsRothoblaasInfo] = useState(false);
  const [showDisableNotificationsInfo, setShowDisableNotificationsInfo] = useState(false);

  const costCenterSwr = costCenterApi.getAdminUserCostCenters(
    user?.id!,
    !!userSettings?.permissions.userCostCenters.read,
  );

  useEffect(() => {
    if (!!userSettings?.permissions.userCostCenters.read) {
      user?.id && costCenterSwr.trigger();
    }
  }, [user?.id]);

  useEffect(() => {
    costCenterSwr.error && dispatch(showErrorNotification(i18n, costCenterSwr.error));
  }, [costCenterSwr.error]);

  const distanceOptions = getMeasurmentTypes(i18n);

  const defaultValues: Partial<UserFormBody> = {
    firstName: user?.firstName,
    lastName: user?.lastName,
    email: user?.email,
    birthDate: user?.birthDate,
    measurmentType: distanceOptions.find((d) => d.value === (user?.distanceInMiles ? "imperial" : "metric")),
    hidden: user?.hidden,
    drivingLicenseExpiration: user?.drivingLicenseExpiration,
    isRothoblaasUser: user?.isRothoblaasUser,
    areNotificationsDisabled: user?.areNotificationsDisabled,
  };

  const hasErrors = !!costCenterSwr?.data?.importErrors && costCenterSwr?.data?.importErrors.length > 0;

  return (
    <>
      <Form<UserFormBody>
        submitText={i18n.t.common.save}
        onSubmit={() => onSubmit}
        defaultValues={defaultValues}
        disabled={isDisabled}
      >
        {({ register, formState: { errors }, reset, watch, setValue, control }) => {
          useEffect(() => {
            !!user && reset(defaultValues);
          }, [user]);

          const hidden = watch("hidden");
          const isRbUser = watch("isRothoblaasUser");
          const areNotificationsDisabled = watch("areNotificationsDisabled");

          const birthDateReadOnly = isRbUser || isDisabled || !!user?.birthDate || isLoading;

          const userForm = (
            <FormFieldsContainer>
              <FormField label={i18n.t.common.firstName} error={errors.firstName} isOptional={isRbUser}>
                {({ labelId, isInvalid, isOptional }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    readOnly={isRbUser || isDisabled || isLoading}
                    {...register("firstName", {
                      required: !isOptional,
                    })}
                  />
                )}
              </FormField>
              <FormField label={i18n.t.common.lastName} error={errors.lastName} isOptional={isRbUser}>
                {({ labelId, isInvalid, isOptional }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    readOnly={isRbUser || isDisabled || isLoading}
                    {...register("lastName", {
                      required: !isOptional,
                    })}
                  />
                )}
              </FormField>
              <FormField
                label={i18n.t.common.costCenter}
                hidden={!user?.id}
                isOptional
                showHelp={
                  userSettings.permissions.userCostCenters.read
                    ? {
                        type: hasErrors ? "error" : "info",
                        onOpen: !isLoading && !isDisabled ? () => setShowCostCenterDialog(true) : undefined,
                      }
                    : undefined
                }
              >
                <Input readOnly value={user?.currentCostCenter ?? ""} />
              </FormField>
              <FormField label={i18n.t.common.email} error={errors.email} isOptional={!isRbUser}>
                {({ labelId, isInvalid, isOptional }) => {
                  const isReadOnly = isDisabled || isLoading;
                  //  ||                    (user?.id && user.isRothoblaasUser && watch("isRothoblaasUser") ? true : false);
                  return (
                    <Input
                      id={labelId}
                      className={isReadOnly && user?.email ? styles.pointer : undefined}
                      isInvalid={isInvalid}
                      readOnly={isReadOnly}
                      {...register("email", {
                        required: !isOptional,
                        validate: (value) => {
                          if (value && isOptional && !isValidEmail(value)) {
                            return i18n.t.common.fieldInvalidValue;
                          }
                          return undefined;
                        },
                      })}
                      onClick={
                        isReadOnly && user?.email ? () => (window.location.href = `mailto:${user?.email}`) : undefined
                      }
                    />
                  );
                }}
              </FormField>
              <FormField label={i18n.t.common.birthdate} error={errors.birthDate} isOptional>
                {({ labelId, isInvalid }) => {
                  const birthDate = watch("birthDate");
                  return birthDateReadOnly ? (
                    <Input id={labelId} readOnly value={birthDate ? formatDateNumeric(birthDate, i18n.language) : ""} />
                  ) : (
                    <DatePicker
                      value={birthDate}
                      clearable
                      isInvalid={isInvalid}
                      onChange={(value) => setValue("birthDate", value)}
                    />
                  );
                }}
              </FormField>
              {user && (
                <FormField
                  label={i18n.t.common.drivingLicenseExpirationDate}
                  error={errors.drivingLicenseExpiration}
                  isOptional
                >
                  {({ labelId, isInvalid }) => {
                    const expiration = watch("drivingLicenseExpiration");
                    return (
                      <Input
                        id={labelId}
                        isInvalid={isInvalid}
                        disabled
                        value={expiration ? formatDateNumeric(expiration, i18n.language) : ""}
                      />
                    );
                  }}
                </FormField>
              )}

              <Controller
                name="measurmentType"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.distances} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Select<MeasurmentType>
                        {...field}
                        id={labelId}
                        options={distanceOptions}
                        isLoading={isLoading}
                        isDisabled={isDisabled || isLoading}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<MeasurmentType>(isInvalid)}
                        menuPosition="fixed"
                      />
                    )}
                  </FormField>
                )}
              />

              <FormField
                label={i18n.t.common.hidden}
                showHelp={{
                  type: "info",
                  onOpen: () => setShowHiddenInfo(true),
                }}
                error={errors.hidden}
                isOptional
              >
                {({ labelId }) => (
                  <Switch
                    id={labelId}
                    color={hidden ? "red" : "blue"}
                    checked={hidden}
                    onChange={(checked) => setValue("hidden", checked)}
                    disabled={isDisabled || isLoading}
                  />
                )}
              </FormField>
              <FormField
                label={i18n.t.common.isRothoblaasUser}
                showHelp={{
                  type: "info",
                  onOpen: () => setShowIsRothoblaasInfo(true),
                }}
                error={errors.isRothoblaasUser}
                isOptional
              >
                {({ labelId }) => (
                  <Switch
                    id={labelId}
                    checked={isRbUser}
                    onChange={(checked) => setValue("isRothoblaasUser", checked)}
                    disabled={isDisabled || isLoading}
                  />
                )}
              </FormField>
              <FormField
                label={i18n.t.common.disableNotifications}
                showHelp={{
                  type: "info",
                  onOpen: () => setShowDisableNotificationsInfo(true),
                }}
                error={errors.areNotificationsDisabled}
                isOptional
              >
                {({ labelId }) => (
                  <Switch
                    id={labelId}
                    color={areNotificationsDisabled ? "red" : "blue"}
                    checked={areNotificationsDisabled}
                    onChange={(checked) => setValue("areNotificationsDisabled", checked)}
                    disabled={isDisabled || isLoading}
                  />
                )}
              </FormField>
            </FormFieldsContainer>
          );

          return user?.salesRole || user?.department || user?.manager ? (
            <div className={styles.form}>
              {userForm}
              <FormFieldsContainer>
                {!!user?.salesRole ? (
                  <FormField label={i18n.t.common.salesRole} isOptional>
                    <Input readOnly value={user?.salesRole ? formatSalesRoles(i18n.t, user.salesRole) : undefined} />
                  </FormField>
                ) : (
                  !!user?.department && (
                    <FormField label={i18n.t.common.department} isOptional>
                      <Input readOnly value={user?.department} />
                    </FormField>
                  )
                )}
                {!!user?.manager && (
                  <>
                    <FormField label={i18n.t.common.managerName} isOptional>
                      <Input readOnly value={user?.manager?.fullName ?? ""} />
                    </FormField>
                    <FormField label={i18n.t.common.managerEmail} isOptional>
                      <Input
                        className={user.manager.email ? styles.pointer : undefined}
                        readOnly
                        value={user.manager.email ?? ""}
                        onClick={
                          user.manager.email
                            ? () => (window.location.href = `mailto:${user?.manager?.email}`)
                            : undefined
                        }
                      />
                    </FormField>
                  </>
                )}
              </FormFieldsContainer>
            </div>
          ) : (
            userForm
          );
        }}
      </Form>

      {showIsRothoblaasInfo && (
        <ModalDialog
          isOnlyTextDialog
          onClose={() => setShowIsRothoblaasInfo(false)}
          title={i18n.t.common.isRothoblaasUser}
          actions={[
            {
              title: i18n.t.common.close,
              onClick: () => setShowIsRothoblaasInfo(false),
            },
          ]}
          isSubmitting={false}
        >
          {i18n.t.common.isRothoblaasUserTexts.map((text, index) => (
            <div key={index}>{text}</div>
          ))}
        </ModalDialog>
      )}

      {showDisableNotificationsInfo && (
        <ModalDialog
          isOnlyTextDialog
          onClose={() => setShowDisableNotificationsInfo(false)}
          title={i18n.t.common.disableNotifications}
          actions={[
            {
              title: i18n.t.common.close,
              onClick: () => setShowDisableNotificationsInfo(false),
            },
          ]}
          isSubmitting={false}
        >
          {i18n.t.common.disableNotificationsText}
        </ModalDialog>
      )}

      {showHiddenInfo && (
        <ModalDialog
          isOnlyTextDialog
          onClose={() => setShowHiddenInfo(false)}
          title={i18n.t.common.hidden}
          actions={[
            {
              title: i18n.t.common.close,
              onClick: () => setShowHiddenInfo(false),
            },
          ]}
          isSubmitting={false}
        >
          {i18n.t.common.hiddenText}
        </ModalDialog>
      )}

      {!!user && !!costCenterSwr.data && showCostCenterDialog && (
        <UserCostCentersDialog
          userCostCentersWithErrors={{ ...costCenterSwr.data, userId: user.id }}
          onClose={(reload) => {
            setShowCostCenterDialog(false);
            if (reload) {
              costCenterSwr.trigger();
              reloadUser?.();
            }
          }}
          onSave={() => {
            costCenterSwr.trigger();
            reloadUser?.();
          }}
        />
      )}
    </>
  );
};

export default UserForm;
